import React, { useState, useMemo } from 'react';
import { TrendingUp, TrendingDown, Maximize2, Minimize2 } from 'lucide-react';
import { LineChart, Line, ResponsiveContainer, Tooltip } from 'recharts';
import { getCompanyContext } from 'utils/setCompanyContext';
import GraficoEvolucaoPercentual from './GraficoEvolucaoPercentual';
import { useReceivableTerm } from 'hooks/useReceivableTerm';

const RecebimentoCompletoCard = ({ resumoTitulos, dadosBrutos, user, loading = false }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const term = useReceivableTerm();

  // Processar dados para sparkline
  const dadosSparkline = useMemo(() => {
    if (!dadosBrutos || !Array.isArray(dadosBrutos) || dadosBrutos.length === 0) {
      return [];
    }

    // Pegar os ultimos 12 meses (excluindo o mes atual)
    const dadosUltimosMeses = dadosBrutos.slice(-13, -1); // Excluir mes atual

    return dadosUltimosMeses.map((item, index) => {
      const titulosEmitidos = item.quantidade_titulos_emitidos || 0;
      const titulosRecebidos = item.quantidade_titulos_recebidos || 0;
      const percentual = titulosEmitidos > 0 ? (titulosRecebidos / titulosEmitidos) * 100 : 0;

      // Formatar mes para exibicao
      let mesFormatado = '';
      if (item.mes) {
        const [ano, mes] = item.mes.split('-');
        const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
        const mesIndex = parseInt(mes, 10) - 1;
        if (mesIndex >= 0 && mesIndex < 12) {
          mesFormatado = `${meses[mesIndex]}/${ano.slice(2)}`;
        } else {
          mesFormatado = item.mes;
        }
      } else {
        mesFormatado = `Mes ${index + 1}`;
      }

      return {
        mes: mesFormatado,
        percentual: parseFloat(percentual.toFixed(1)),
        dadosOriginais: item
      };
    });
  }, [dadosBrutos]);

  // Calcular estatisticas
  const estatisticas = useMemo(() => {
    if (dadosSparkline.length === 0) return null;

    const percentuais = dadosSparkline.map(d => d.percentual);
    const media = percentuais.reduce((acc, val) => acc + val, 0) / percentuais.length;
    const maior = Math.max(...percentuais);
    const menor = Math.min(...percentuais);
    const atual = resumoTitulos?.total_titulos_recebidos && resumoTitulos?.total_titulos_emitidos
      ? (resumoTitulos.total_titulos_recebidos / resumoTitulos.total_titulos_emitidos) * 100
      : 0;

    const delta = atual - media;
    const tendencia = dadosSparkline.length >= 2
      ? dadosSparkline[dadosSparkline.length - 1].percentual - dadosSparkline[dadosSparkline.length - 2].percentual
      : 0;

    return {
      atual: parseFloat(atual.toFixed(1)),
      media: parseFloat(media.toFixed(1)),
      maior: parseFloat(maior.toFixed(1)),
      menor: parseFloat(menor.toFixed(1)),
      delta: parseFloat(delta.toFixed(1)),
      tendencia: parseFloat(tendencia.toFixed(1))
    };
  }, [dadosSparkline, resumoTitulos]);

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Funcao para determinar a saudacao baseada no horario
  const getSaudacao = () => {
    const hora = new Date().getHours();
    if (hora >= 5 && hora < 12) return 'Bom dia';
    if (hora >= 12 && hora < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  // Funcao para obter informacoes relevantes do usuario e empresa
  const getInfoUsuario = () => {
    const nome = user?.full_name || user?.name || user?.nome || user?.username || 'Usuario';

    let empresa = '';
    try {
      const companyContext = getCompanyContext();
      if (companyContext?.companies && companyContext.companies.length > 0) {
        if (companyContext.isGroup || companyContext.isConsolidated) {
          empresa = 'Horizon Group';
        } else {
          empresa = companyContext.companies[0].name;
        }
      }
    } catch (error) {
      console.warn('Erro ao obter contexto da empresa:', error);
      empresa = user?.company || user?.empresa || user?.corporate_email?.split('@')[1] || '';
    }

    return { nome, empresa };
  };

  const { nome } = getInfoUsuario();
  const saudacao = getSaudacao();

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const mes = data.mes;
      const percentual = data.percentual;
      const dadosOriginais = data.dadosOriginais;

      const titulosEmitidos = dadosOriginais?.quantidade_titulos_emitidos || 0;
      const titulosRecebidos = dadosOriginais?.quantidade_titulos_recebidos || 0;

      return (
        <div className="bg-slate-900/90 p-3 rounded-lg shadow-lg border border-[color:var(--border)]">
          <p className="text-sm font-medium text-[color:var(--text-primary)]">{mes}</p>
          <p className="text-lg font-bold text-[color:var(--status-good)]">{percentual}%</p>
          <p className="text-xs text-[color:var(--text-muted)]">
            {titulosRecebidos.toLocaleString('pt-BR')} de {titulosEmitidos.toLocaleString('pt-BR')} {term.pluralLower}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading || !estatisticas) {
    return (
      <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur p-4 animate-pulse">
        <div className="h-3 w-32 bg-[color:var(--surface-muted)] rounded mb-3"></div>
        <div className="h-6 w-56 bg-[color:var(--surface-muted)] rounded mb-2"></div>
        <div className="h-4 w-24 bg-[color:var(--surface-muted)] rounded mb-4"></div>
        <div className="h-10 w-32 bg-[color:var(--surface-muted)] rounded"></div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-[color:var(--border)] bg-[color:var(--surface)] shadow-[0_4px_12px_rgba(0,0,0,0.14)] backdrop-blur transition-colors duration-200 hover:bg-[color:var(--surface-muted)]">
      <div className="p-3 sm:p-3.5">
        <div className="grid grid-cols-2 gap-3 items-start lg:grid-cols-2 lg:items-center">
          <div className="text-left max-w-2xl self-start">
            <div className="text-[10px] sm:text-sm uppercase tracking-[0.22em] text-[color:var(--text-muted)] mb-2">
              Dashboard Financeiro
            </div>
            <div className="text-lg sm:text-xl font-semibold text-[color:var(--text-primary)] mb-1 tracking-tight">
              {saudacao}, <span className="text-sky-300">{nome}</span>!
            </div>
            <div className="hidden sm:block text-sm text-[color:var(--text-muted)] max-w-md">
              Bem-vindo ao sistema de gestão integrada
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_max-content] gap-2.5 sm:gap-3.5 lg:gap-4 lg:pl-2.5 lg:border-l lg:border-[color:var(--border)] items-start self-start -mt-0.5 sm:mt-0">
            <div className={`flex flex-col items-end gap-2 order-2 justify-start text-right sm:justify-self-end ${isExpanded ? 'sm:col-span-2 sm:justify-self-start' : ''}`}>
              <div className="flex items-center gap-2 justify-start text-[11px] font-semibold text-[color:var(--text-muted)] uppercase tracking-wide">
                <span>Recebimento (Atual)</span>
                <button
                  onClick={handleExpand}
                  className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)] hover:bg-[color:var(--surface-muted)] rounded-md transition-colors"
                  title={isExpanded ? 'Minimizar grafico' : 'Ampliar grafico'}
                >
                  {isExpanded ? (
                    <Minimize2 className="w-4 h-4" />
                  ) : (
                    <Maximize2 className="w-4 h-4" />
                  )}
                </button>
              </div>

              <div className="flex items-center gap-2 justify-start">
                <div className={`text-2xl sm:text-4xl font-bold ${
                  estatisticas.atual >= 85
                    ? 'text-[color:var(--status-good)]'
                    : estatisticas.atual >= 70
                    ? 'text-[color:var(--status-warn)]'
                    : 'text-[color:var(--status-bad)]'
                }`}>
                  {estatisticas.atual}%
                </div>
                <div className="flex items-center gap-1">
                  {estatisticas.tendencia >= 0 ? (
                    <TrendingUp className="w-4 h-4 text-[color:var(--status-good)]" />
                  ) : (
                    <TrendingDown className="w-4 h-4 text-[color:var(--status-bad)]" />
                  )}
                  <span className={`text-[11px] font-medium ${
                    estatisticas.tendencia >= 0 ? 'text-[color:var(--status-good)]' : 'text-[color:var(--status-bad)]'
                  }`}>
                    {Math.abs(estatisticas.tendencia).toFixed(1)}pp
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-2 justify-start">
                <div className={`w-2 h-2 rounded-full ${
                  estatisticas.atual >= 85
                    ? 'bg-[color:var(--status-good)]'
                    : estatisticas.atual >= 70
                    ? 'bg-[color:var(--status-warn)]'
                    : 'bg-[color:var(--status-bad)]'
                }`}></div>
                <span className={`text-[11px] font-medium ${
                  estatisticas.atual >= 85
                    ? 'text-[color:var(--status-good)]'
                    : estatisticas.atual >= 70
                    ? 'text-[color:var(--status-warn)]'
                    : 'text-[color:var(--status-bad)]'
                }`}>
                  {estatisticas.atual >= 85 ? 'Muito Bom' : estatisticas.atual >= 70 ? 'Bom' : 'Atenção'}
                </span>
              </div>

              <div className="text-[11px] text-[color:var(--text-muted)]">
                {resumoTitulos?.total_titulos_recebidos?.toLocaleString('pt-BR', {
                  notation: 'compact',
                  maximumFractionDigits: 1
                }) || 0} de {resumoTitulos?.total_titulos_emitidos?.toLocaleString('pt-BR', {
                  notation: 'compact',
                  maximumFractionDigits: 1
                }) || 0} {term.pluralLower}
              </div>
            </div>

            {!isExpanded && (
              <div className="hidden sm:flex flex-col items-start order-1">
                <div className="text-xs font-semibold text-[color:var(--text-muted)] uppercase tracking-wide mb-2 text-left">
                  Últimos 12 meses
                </div>
                <div className="w-full sm:w-72 h-16 bg-[color:var(--surface-muted)] rounded-lg p-2 border border-[color:var(--border)]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dadosSparkline}>
                      <Tooltip content={<CustomTooltip />} />
                      <Line
                        type="monotone"
                        dataKey="percentual"
                        stroke="#3b82f6"
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: '#3b82f6', stroke: '#fff', strokeWidth: 1 }}
                        activeDot={{ r: 4, fill: '#1d4ed8', stroke: '#fff', strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-[color:var(--border)]">
          <GraficoEvolucaoPercentual dadosGrafico={dadosBrutos} dadosBrutos={dadosBrutos} />
        </div>
      )}
    </div>
  );
};

export default RecebimentoCompletoCard;
